import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, IconButton, List, ListItem, ListSubheader } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { v4 as uuidv4 } from 'uuid';
import { predefinedShots } from '../../services/shotService';

const PredefinedShotsEditor = () => {
    const navigate = useNavigate();
    const [editingShot, setEditingShot] = useState(null);
    const [newCategory, setNewCategory] = useState('');
    const [shots, setShots] = useState(() =>
        predefinedShots.map((shot) => ({
            ...shot,
            id: shot.id || uuidv4(), // Assign a UUID if id is missing
        }))
    );

    const addShot = (category) => {
        const newShot = { category, label: 'New Shot', id: uuidv4() }; // Create new shot
        setShots((prev) => [...prev, newShot]); // Add to shots list
        setEditingShot(newShot.id); // Put the new shot in edit mode
    };

    const addCategory = () => {
        if (!newCategory.trim()) return;
        setShots((prev) => [
            { category: newCategory, id: uuidv4() }, // New category at the top
            ...prev, // Keep existing categories
        ]);
        setNewCategory('');
    };

    const editShot = (id, label) => {
        const updatedShots = shots.map((shot) =>
            shot.id === id ? { ...shot, label } : shot
        );
        setShots(updatedShots);
    };

    const deleteShot = (id) => {
        if (!id) {
            console.warn('deleteShot: Invalid ID provided.');
            return;
        }

        // Validate if the shot with the given ID exists
        const shotExists = shots.some((shot) => shot.id === id);
        if (!shotExists) {
            console.warn(`deleteShot: Shot with ID ${id} does not exist.`);
            return;
        }

        // Filter out only the shot with the matching ID
        const updatedShots = shots.filter((shot) => shot.id !== id);
        setShots(updatedShots);
    };

    // Save the custom shot list (mock saving)
    const saveShotList = () => {
        console.log('Saving custom shot list:', shots);
        // Call shotService or an API to save the custom shot list
    };

    //const handleBackToSettings = () => {
    //    navigate('/settings');
    //};

    return (
        <Box sx={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 4, marginBottom: '80px', }}>
            {/* Back to Personnel Button 
            <Button
                variant="text"
                onClick={handleBackToSettings}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Settings
            </Button>
*/}

            <Typography variant="h4" component="h1" gutterBottom>
                Predefined Shots Editor
            </Typography>
            {/* Category Input */}
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <TextField
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add new category"
                    size="small"
                    sx={{
                        marginRight: '8px',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                    }}
                />
                <Button variant="contained" onClick={addCategory} startIcon={<AddCircleOutlineIcon />}>
                    Add Category
                </Button>
            </Box>
            <List>
                {Array.from(
                    shots.reduce((map, shot) => {
                        const { category } = shot;
                        if (!map.has(category)) map.set(category, []);
                        map.get(category).push(shot);
                        return map;
                    }, new Map())
                ).map(([category, shotsInCategory], categoryIndex) => (
                    <Box key={categoryIndex} sx={{ marginBottom: '16px' }}>
                        <ListSubheader
                            sx={{
                                backgroundColor: '#ffffff', //'#f5f5f5',
                                padding: '0px 16px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                fontWeight: 'bold',
                                marginBottom: '8px',
                                border: '1px solid #ddd',
                            }}
                        >
                            {category}
                        </ListSubheader>
                        <List>
                            {shotsInCategory
                                .filter((shot) => shot.label)
                                .map((shot) => (
                                    <ListItem key={shot.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                        {editingShot === shot.id ? (
                                            <>
                                                <TextField
                                                    value={shot.label || ''}
                                                    onChange={(e) => editShot(shot.id, e.target.value)}
                                                    size="small"
                                                    sx={{ marginRight: '8px', flexGrow: 1 }}
                                                />
                                                <IconButton onClick={() => setEditingShot(null)}>
                                                    <SaveIcon />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <Typography sx={{ flexGrow: 1 }}>{shot.label}</Typography>
                                                <IconButton onClick={() => setEditingShot(shot.id)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => deleteShot(shot.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </ListItem>
                                ))}
                            <Button
                                onClick={() => addShot(category)}
                                startIcon={<AddCircleOutlineIcon />}
                                sx={{ marginLeft: '16px', marginTop: '8px' }}
                            >
                                Add Shot
                            </Button>
                        </List>
                    </Box>
                ))}
            </List>
            <Box sx={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={saveShotList}>
                    Save Shot List
                </Button>
            </Box>
        </Box>
    );
};

export default PredefinedShotsEditor;